(() => {
  if (customElements.get('sac-garage-drawer')) return;
  const ordinaryClick = event => !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  const preserveStyle = (element, properties) => properties.map(name => [name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name)]);
  const restoreStyle = (element, values) => values.forEach(([name, value, priority]) => value ? element.style.setProperty(name, value, priority) : element.style.removeProperty(name));
  const resources = new Map();
  const failedResources = new Set();
  const failedNodes = new WeakSet();
  let assetRequest = 0;
  let instance = 0;
  const abortable = (promise, signal) => new Promise((resolve, reject) => {
    const abort = () => reject(new Error('Garage closed'));
    if (signal.aborted) { abort(); return; }
    signal.addEventListener('abort', abort, { once: true });
    promise.then(value => { signal.removeEventListener('abort', abort); resolve(value); }, error => { signal.removeEventListener('abort', abort); reject(error); });
  });
  const loadResource = (value, kind, apiReady) => {
    if (typeof value !== 'string' || !value) return Promise.reject(new Error('Missing asset'));
    const url = new URL(value, window.location.href);
    if (url.username || url.password || (url.protocol !== 'https:' && url.origin !== window.location.origin)) return Promise.reject(new Error('Invalid asset'));
    const key = `${kind}:${url.href}`;
    const nodes = () => [...document.querySelectorAll(kind === 'style' ? 'link[rel="stylesheet"][href]' : 'script[src]')].filter(node => node.__sacGarageAssetKey === key || (kind === 'style' ? node.href : node.src) === url.href);
    const ready = () => {
      try { return kind === 'script' ? apiReady() === true : nodes().some(node => node.sheet || node.__sacGarageAssetReady); } catch { return false; }
    };
    if (ready()) return Promise.resolve();
    if (resources.has(key)) return resources.get(key);
    const watch = (node, owned, timeout) => new Promise((resolve, reject) => {
      let settled = false, timer, poll;
      const done = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer); clearTimeout(poll);
        node.removeEventListener('load', loaded);
        node.removeEventListener('error', failed);
        if (error) { failedNodes.add(node); if (owned) node.remove(); reject(error); }
        else { node.__sacGarageAssetReady = true; resolve(); }
      };
      const loaded = () => done(kind === 'script' && !ready() ? new Error('Asset did not initialize') : null);
      const failed = () => done(ready() ? null : new Error('Asset unavailable'));
      const check = () => { if (ready()) done(); else poll = setTimeout(check, 100); };
      node.addEventListener('load', loaded, { once: true });
      node.addEventListener('error', failed, { once: true });
      timer = setTimeout(failed, timeout);
      check();
      if (owned && !settled) document.head.append(node);
    });
    const run = async () => {
      // A deferred script may already have dispatched load/error before this
      // drawer opens. Observe briefly, then recover without waiting forever.
      const existing = nodes().find(node => !failedNodes.has(node));
      if (existing && !failedResources.has(key)) {
        try { await watch(existing, false, 1200); return; } catch { failedResources.add(key); }
      }
      for (let attempt = 0; attempt < 2; attempt++) {
        if (ready()) return;
        const requestUrl = new URL(url.href);
        if (failedResources.has(key)) requestUrl.searchParams.set('_sac_garage_retry', `${Date.now()}-${++assetRequest}`);
        const node = document.createElement(kind === 'style' ? 'link' : 'script');
        node.__sacGarageAssetKey = key;
        if (kind === 'style') { node.rel = 'stylesheet'; node.href = requestUrl.href; }
        else { node.src = requestUrl.href; node.async = true; }
        try { await watch(node, true, 7000); return; }
        catch (error) { failedResources.add(key); if (attempt === 1) throw error; }
      }
    };
    const promise = run();
    resources.set(key, promise);
    const forget = () => { if (resources.get(key) === promise) resources.delete(key); };
    promise.then(forget, forget);
    return promise;
  };
  const prepareRoot = (html, url, prefix) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const roots = parsed.querySelectorAll('[data-storefront-garage]');
    if (!parsed.body?.hasAttribute('data-sac-garage-frame') || roots.length !== 1) throw new Error('Unexpected Garage page');
    const root = roots[0];
    if (root.dataset.garageEmbedded !== 'true') throw new Error('Unexpected Garage mode');
    for (const node of root.querySelectorAll('script')) {
      if (node.type === 'application/json' && node.hasAttribute('data-garage-strings') && !node.hasAttribute('src')) JSON.parse(node.textContent);
      else node.remove();
    }
    if (root.querySelectorAll('script[data-garage-strings]').length !== 1) throw new Error('Missing Garage labels');
    root.querySelectorAll('iframe,object,embed,base,meta,link,style,template').forEach(node => node.remove());
    const ids = new Map();
    const nodes = [root, ...root.querySelectorAll('*')];
    for (const node of nodes) if (node.id) {
      if (ids.has(node.id)) throw new Error('Duplicate Garage identifier');
      ids.set(node.id, `${prefix}${node.id}`);
    }
    const references = ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns', 'aria-activedescendant', 'aria-details', 'aria-errormessage', 'aria-flowto', 'for', 'list', 'form', 'headers'];
    for (const node of nodes) {
      if (node.id) node.id = ids.get(node.id);
      for (const attribute of [...node.attributes]) {
        if (/^on/i.test(attribute.name) || attribute.name === 'srcdoc') node.removeAttribute(attribute.name);
      }
      for (const name of references) if (node.hasAttribute(name)) node.setAttribute(name, node.getAttribute(name).split(/\s+/).map(id => ids.get(id) || id).join(' '));
      for (const name of ['href', 'xlink:href', 'src', 'action', 'formaction']) {
        if (!node.hasAttribute(name)) continue;
        const value = node.getAttribute(name);
        if (value.startsWith('#')) { if (ids.has(value.slice(1))) node.setAttribute(name, `#${ids.get(value.slice(1))}`); continue; }
        const target = new URL(value, url);
        if (!['http:', 'https:', 'mailto:', 'tel:'].includes(target.protocol)) node.removeAttribute(name);
        else node.setAttribute(name, target.href);
      }
    }
    return document.importNode(root, true);
  };

  class SacGarageDrawer extends HTMLElement {
    waitForMarkup() {
      if (this.startupObserver || typeof window.MutationObserver !== 'function') return;
      this.startupObserver = new window.MutationObserver(() => this.connectedCallback());
      this.startupObserver.observe(this, {childList: true, subtree: true});
      this.startupTimeout = setTimeout(() => this.stopWaitingForMarkup(), 10000);
    }

    stopWaitingForMarkup() {
      this.startupObserver?.disconnect();
      this.startupObserver = null;
      clearTimeout(this.startupTimeout);
      this.startupTimeout = null;
    }

    connectedCallback() {
      if (this.controller) return;
      this.__sacGarageReady = false;
      this.dialog = this.querySelector('[data-sac-garage-drawer]');
      if (!this.dialog) { this.waitForMarkup(); return; }
      if (typeof this.dialog.showModal !== 'function') { this.stopWaitingForMarkup(); return; }
      const hooks = ['title', 'frame', 'loading', 'error', 'saving', 'content', 'full', 'close', 'retry'];
      if (hooks.some(name => !this.querySelector(`[data-garage-drawer-${name}]`))) { this.waitForMarkup(); return; }
      this.stopWaitingForMarkup();
      try {
        this.url = new URL(this.dataset.garageUrl, window.location.href);
        if (this.url.origin !== window.location.origin || this.url.username || this.url.password || !/\/pages\/my-garage\/?$/.test(this.url.pathname)) return;
        this.url.search = '';
        this.url.hash = '';
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview && /^\d+$/.test(preview)) this.url.searchParams.set('preview_theme_id', preview);
      } catch { return; }
      this.titleElement = this.querySelector('[data-garage-drawer-title]');
      this.mount = this.querySelector('[data-garage-drawer-frame]');
      this.loading = this.querySelector('[data-garage-drawer-loading]');
      this.error = this.querySelector('[data-garage-drawer-error]');
      this.saving = this.querySelector('[data-garage-drawer-saving]');
      this.content = this.querySelector('[data-garage-drawer-content]');
      this.full = this.querySelector('[data-garage-drawer-full]');
      this.full.href = this.url.href;
      this.idPrefix = `sac-garage-drawer-${++instance}-`;
      this.generation = 0;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      document.addEventListener('click', event => {
        if (!ordinaryClick(event)) return;
        const anchor = event.target.closest?.('a[data-sac-garage-entry]');
        if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;
        let target;
        try { target = new URL(anchor.href, window.location.href); } catch { return; }
        if (target.origin !== this.url.origin || target.pathname !== this.url.pathname || target.username || target.password) return;
        if (this.open(anchor)) event.preventDefault();
      }, options);
      this.querySelector('[data-garage-drawer-close]').addEventListener('click', () => this.requestClose(), options);
      this.querySelector('[data-garage-drawer-retry]').addEventListener('click', () => this.load(), options);
      this.full.addEventListener('click', event => {
        if (ordinaryClick(event) && this.busy()) { event.preventDefault(); this.showSaving(); }
      }, options);
      this.mount.addEventListener('click', event => {
        if (!ordinaryClick(event)) return;
        const link = event.target.closest?.('a[href]');
        const href = link?.getAttribute('href');
        if (!href?.startsWith(`#${this.idPrefix}`)) return;
        const target = [...this.mount.querySelectorAll('[id]')].find(node => node.id === href.slice(1));
        if (target) { event.preventDefault(); target.scrollIntoView({ block: 'start', behavior: 'instant' }); }
      }, options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        if (!this.childDialogOpen()) this.requestClose();
      }, options);
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.cleanup(true); }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.requestClose();
      }, options);
      window.addEventListener('pagehide', () => {
        this.cleanup(false);
        if (this.dialog.open) this.dialog.close();
      }, options);
      this.__sacGarageReady = true;
      try { if (window.SacGarageStartup?.ready?.(this) === true) return; } catch {}
      const queued = this.__sacGaragePendingTrigger;
      delete this.__sacGaragePendingTrigger;
      if (queued?.isConnected) {
        try {
          const target = new URL(queued.href, window.location.href);
          if (target.origin === this.url.origin && target.pathname === this.url.pathname && !target.username && !target.password) this.open(queued);
        } catch {}
      }
    }

    open(trigger) {
      if (this.dialog.open) return true;
      this.trigger = trigger;
      document.querySelectorAll('.sac-header details[open]').forEach(details => { details.open = false; });
      try { this.dialog.showModal(); } catch { return false; }
      this.dataset.sacGarageTrackingOpen = 'drawer';
      try { window.SacGarageTracking?.opened?.(this, 'drawer'); } catch {}
      this.scrollState = { x: window.scrollX, y: window.scrollY };
      this.bodyStyle = preserveStyle(document.body, ['position', 'top', 'left', 'right', 'width', 'overflow', 'box-sizing', 'padding-right']);
      this.rootStyle = preserveStyle(document.documentElement, ['overflow']);
      const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      Object.entries({ position: 'fixed', top: `${-this.scrollState.y}px`, left: `${-this.scrollState.x}px`, right: '0', width: '100%', overflow: 'hidden', 'box-sizing': 'border-box', 'padding-right': `${padding + gap}px` }).forEach(([name, value]) => document.body.style.setProperty(name, value));
      document.documentElement.style.setProperty('overflow', 'hidden');
      this.saving.hidden = true;
      this.titleElement.focus({ preventScroll: true });
      this.load();
      return true;
    }

    busy() {
      return this.garage?.dataset.garageMutationBusy === 'true';
    }

    childDialogOpen() {
      return Boolean(this.garage?.querySelector('dialog[open]'));
    }

    showSaving() {
      this.saving.hidden = false;
      if (this.savingObserver || !this.garage || typeof window.MutationObserver !== 'function') return;
      const garage = this.garage;
      this.savingObserver = new window.MutationObserver(() => {
        if (this.garage === garage && this.busy()) return;
        this.saving.hidden = true;
        this.savingObserver?.disconnect();
        this.savingObserver = null;
      });
      this.savingObserver.observe(garage, {attributes: true, attributeFilter: ['data-garage-mutation-busy']});
    }

    requestClose() {
      if (!this.dialog.open) return;
      if (this.busy()) { this.showSaving(); return; }
      this.dialog.close();
      this.cleanup(true);
    }

    clearContent() {
      this.savingObserver?.disconnect();
      this.savingObserver = null;
      this.saving.hidden = true;
      this.generation++;
      clearTimeout(this.timeout);
      this.timeout = null;
      this.loadController?.abort();
      this.loadController = null;
      if (this.garage) { try { window.SacStorefrontGarage?.destroy(this.garage); } catch {} }
      this.garage = null;
      this.mount.replaceChildren();
      this.mount.hidden = true;
    }

    async load() {
      if (!this.dialog.open || this.busy()) return;
      this.clearContent();
      const generation = this.generation;
      const current = () => this.generation === generation && this.dialog.open && this.isConnected;
      this.loading.hidden = false;
      this.error.hidden = true;
      this.saving.hidden = true;
      this.content.setAttribute('aria-busy', 'true');
      const url = new URL(this.url.href);
      url.searchParams.set('view', 'garage-panel');
      this.loadController = new AbortController();
      const signal = this.loadController.signal;
      const fail = () => {
        if (!current()) return;
        this.clearContent();
        this.loading.hidden = true;
        this.error.hidden = false;
        this.content.removeAttribute('aria-busy');
      };
      this.timeout = setTimeout(fail, 20000);
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal, headers: { Accept: 'text/html' } });
        if (!current()) return;
        const destination = new URL(response.url);
        if (!response.ok || destination.origin !== url.origin || destination.pathname !== url.pathname || destination.searchParams.get('view') !== 'garage-panel' || !response.headers.get('content-type')?.toLowerCase().includes('text/html') || Number(response.headers.get('content-length')) > 2097152) throw new Error('Unexpected response');
        const html = await response.text();
        if (!current()) return;
        if (!html || html.length > 2097152) throw new Error('Unexpected response size');
        await abortable(Promise.all([
          loadResource(this.dataset.garageStyleUrl, 'style'),
          loadResource(this.dataset.panelStyleUrl, 'style'),
          loadResource(this.dataset.memberScriptUrl, 'script', () => typeof window.SacGarageGuestRewards?.redeemPending === 'function'),
          loadResource(this.dataset.garageScriptUrl, 'script', () => typeof window.SacStorefrontGarage?.mount === 'function' && typeof window.SacStorefrontGarage?.destroy === 'function')
        ]), signal);
        if (!current()) return;
        if (typeof window.SacStorefrontGarage?.mount !== 'function' || typeof window.SacStorefrontGarage?.destroy !== 'function') throw new Error('Garage unavailable');
        const root = prepareRoot(html, url.href, `${this.idPrefix}${generation}-`);
        this.garage = root;
        this.mount.append(root);
        if (window.SacStorefrontGarage.mount(root) !== true) throw new Error('Garage could not start');
        clearTimeout(this.timeout);
        this.timeout = null;
        this.loadController = null;
        this.loading.hidden = true;
        this.error.hidden = true;
        this.mount.hidden = false;
        this.mount.scrollTop = 0;
        this.content.removeAttribute('aria-busy');
      } catch { fail(); }
    }

    cleanup(restoreFocus) {
      delete this.dataset.sacGarageTrackingOpen;
      try { window.SacGarageTracking?.closed?.(this); } catch {}
      this.clearContent();
      this.loading.hidden = true;
      this.error.hidden = true;
      this.saving.hidden = true;
      this.content.removeAttribute('aria-busy');
      if (this.bodyStyle) {
        restoreStyle(document.body, this.bodyStyle);
        restoreStyle(document.documentElement, this.rootStyle);
        this.bodyStyle = null;
        this.rootStyle = null;
        window.scrollTo(this.scrollState.x, this.scrollState.y);
      }
      const trigger = this.trigger;
      this.trigger = null;
      if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.stopWaitingForMarkup();
      this.__sacGarageReady = false;
      delete this.__sacGaragePendingTrigger;
      if (!this.controller) return;
      this.cleanup(false);
      if (this.dialog.open) this.dialog.close();
      this.controller.abort();
      this.controller = null;
    }
  }
  customElements.define('sac-garage-drawer', SacGarageDrawer);
})();
